import { EFinancingProgram } from '@if/core/types'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQueryClient } from '@tanstack/react-query'
import { CreditRslaWorksheet, EditCreditRslaWorksheetDto } from '@src/data/types/CreditRslaWorksheet'
import { FullCreditApplication } from '../../types'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'

const baseUrl = '/Applications/credit-rsla/{creditApplicationId}/Worksheet'

// Query Keys
export const creditRslaWorksheetKeys = {
  all: ['creditRslaWorksheet'] as const,
}

// API Functions
export async function getCreditRslaWorksheet(creditApplicationId: string): Promise<CreditRslaWorksheet> {
  const apiClient = getApiClient()
  const url = baseUrl.replace('{creditApplicationId}', creditApplicationId)
  const response = await apiClient.get<CreditRslaWorksheet>(url)
  return response.data
}

export async function createCreditRslaWorksheet(data: EditCreditRslaWorksheetDto): Promise<CreditRslaWorksheet> {
  const apiClient = getApiClient()
  const url = baseUrl.replace('{creditApplicationId}', data.creditApplicationId!)
  const response = await apiClient.post<CreditRslaWorksheet>(url, data)
  return response.data
}

export async function updateCreditRslaWorksheet(data: EditCreditRslaWorksheetDto): Promise<CreditRslaWorksheet> {
  const apiClient = getApiClient()
  const url = baseUrl.replace('{creditApplicationId}', data.creditApplicationId!)
  const response = await apiClient.put<CreditRslaWorksheet>(url, data)
  return response.data
}

// React-Query Hooks
export function useCreateCreditRslaWorksheet(): [
  MutationFunction<CreditRslaWorksheet, EditCreditRslaWorksheetDto>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: createCreditRslaWorksheet,
    onSuccess: (createdWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(createdWorksheet.creditApplicationId, EFinancingProgram.CreditRsla)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: createdWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useUpdateCreditRslaWorksheet(): [
  MutationFunction<CreditRslaWorksheet, EditCreditRslaWorksheetDto>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateCreditRslaWorksheet,
    onSuccess: (updatedWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(updatedWorksheet.creditApplicationId, EFinancingProgram.CreditRsla)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: updatedWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}
